import { REACTION_OPTIONS } from "../constants/reactions"
import { EmojiOptionName } from "../interfaces/emojiOptionName.type"
import { ReactionOption } from "../interfaces/reactionOption.interface"
import { computed, ComputedRef, ref, Ref, watch } from "vue"
import { Emoji } from "suus-api"

interface UseReactionOptionsParams {
  source: ComputedRef<Emoji>
  createReaction: (newVal: EmojiOptionName) => void
  deleteReaction: (prevVal: EmojiOptionName) => void
  updateReaction: (prevVal: EmojiOptionName, newVal: EmojiOptionName) => void
}

export default function useReactionOptions({
  source,
  createReaction,
  deleteReaction,
  updateReaction,
}: UseReactionOptionsParams) {
  const DEFAULT_OPTION_NAME: EmojiOptionName = "default"

  const options: ReactionOption[] = REACTION_OPTIONS

  function getOptionByName(
    optionName: EmojiOptionName = DEFAULT_OPTION_NAME
  ): ReactionOption {
    return options.find((item) => optionName === item.name)
  }

  const selectedOption: Ref<ReactionOption> = ref(
    getOptionByName(source.value ?? DEFAULT_OPTION_NAME)
  )
  watch(source, (newVal) => {
    selectedOption.value = getOptionByName(newVal ?? DEFAULT_OPTION_NAME)
  })

  const selectedOptionIndex = computed(() =>
    getOptionIndex(selectedOption.value?.name)
  )

  function getOptionIndex(
    optionName: EmojiOptionName = DEFAULT_OPTION_NAME
  ): number {
    return options.findIndex((option) => option.name === optionName)
  }

  function mutateReaction(prevVal: EmojiOptionName, newVal: EmojiOptionName) {
    if (prevVal === DEFAULT_OPTION_NAME) createReaction(newVal)
    else if (prevVal === newVal) deleteReaction(prevVal)
    else updateReaction(prevVal, newVal)
  }

  function selectReaction(selectedReaction: Emoji): void {
    const _selectedReaction =
      selectedReaction === DEFAULT_OPTION_NAME
        ? options[0].name
        : selectedReaction
    const prevReaction = selectedOption.value?.name
    mutateReaction(prevReaction, _selectedReaction)
  }

  return {
    selectedOption,
    selectedOptionIndex,
    options,
    selectReaction,
  }
}
